// useThemeByEmpresa.ts
// Detecta a empresa do usuário (KADESH / IMBISEG) e retorna as cores do tema

import { useState, useEffect, useLayoutEffect } from 'react';
import { getUserEmpresaAPI } from '../services/authService';

export interface ThemeColors {
  primary: string;       // Cor principal (barras, botões)
  primaryLight: string;  // Fundo claro
  primaryDark: string;   // Hover/ativo
  accent: string;        // Cor de destaque
  chartGradient: [string, string];
  name: string;          // Nome legível
  // Sidebar styling properties
  sidebarBg: string;
  sidebarLinkColor: string;
  sidebarActiveBg: string;
  sidebarActiveColor: string;
  sidebarActiveBorderColor: string;
  sidebarShadow: string;
  mobileMenuBg: string;
  // Logo properties
  sidebarLogoHeight: string;
  sidebarLogoMobileHeight: string;
  sidebarLogoPaddingX: string;
  sidebarLogoPaddingBottom: string;
}

const THEMES: Record<string, ThemeColors> = {
  KADESH: {
    primary: '#b91c1c',
    primaryLight: '#fef2f2',
    primaryDark: '#991b1b',
    accent: '#1e293b', // Azul Navy no tema claro
    chartGradient: ['#dc2626', '#991b1b'],
    name: 'Kadesh',
    sidebarBg: '#fdfdff',
    sidebarLinkColor: '#b0b0b0',
    sidebarActiveBg: '#910e10',
    sidebarActiveColor: '#ffffff',
    sidebarActiveBorderColor: 'transparent',
    sidebarShadow: '#b1b1b1 0px 0px 10px 0px',
    mobileMenuBg: '#910e10',
    sidebarLogoHeight: '28px',
    sidebarLogoMobileHeight: '30px',
    sidebarLogoPaddingX: '2px',
    sidebarLogoPaddingBottom: '20px'
  },
  IMBISEG: {
    primary: '#d97706', // Laranja/Amarelo Imbiseg
    primaryLight: '#fffbeb',
    primaryDark: '#b45309',
    accent: '#1e293b', // Azul Navy no tema claro
    chartGradient: ['#f59e0b', '#d97706'],
    name: 'Imbiseg',
    sidebarBg: '#fdfdff', // White sidebar
    sidebarLinkColor: '#b0b0b0', // Grayish/silver
    sidebarActiveBg: '#1e293b', // Navy background no tema claro
    sidebarActiveColor: '#ffffff', // White icon and text
    sidebarActiveBorderColor: '#1e293b', // Navy border no tema claro
    sidebarShadow: '#b1b1b1 0px 0px 10px 0px',
    mobileMenuBg: '#1e293b',
    sidebarLogoHeight: '45px', // Bigger height for square logo
    sidebarLogoMobileHeight: '50px',
    sidebarLogoPaddingX: '2px', // Less padding to allow horizontal space
    sidebarLogoPaddingBottom: '10px' // Smaller bottom margin for Imbiseg
  },
  EXPORTAÇÃO: {
    primary: '#0891b2',
    primaryLight: '#ecfeff',
    primaryDark: '#0e7490',
    accent: '#1677ff',
    chartGradient: ['#06b6d4', '#0891b2'],
    name: 'Exportação',
    sidebarBg: '#0f172a', // Dark Slate
    sidebarLinkColor: '#64748b',
    sidebarActiveBg: '#0891b2', // Cyan active bg
    sidebarActiveColor: '#ffffff',
    sidebarActiveBorderColor: 'transparent',
    sidebarShadow: 'rgba(0, 0, 0, 0.3) 0px 0px 15px 0px',
    mobileMenuBg: '#0891b2',
    sidebarLogoHeight: '25px',
    sidebarLogoMobileHeight: '30px',
    sidebarLogoPaddingX: '2px',
    sidebarLogoPaddingBottom: '20px'
  }
};

const getInitialThemeName = () => {
  const cached = localStorage.getItem('theme_empresa_cache');
  if (cached && THEMES[cached.toUpperCase()]) {
    return cached.toUpperCase();
  }
  return 'KADESH';
};

export function useThemeByEmpresa() {
  const initialName = getInitialThemeName();
  const [theme, setTheme] = useState<ThemeColors>(THEMES[initialName] || THEMES.KADESH);
  const [empresa, setEmpresa] = useState<string>(initialName);
  const [loading, setThemeLoading] = useState(true);

  const [themeMode, setThemeModeState] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('app_theme_mode');
    if (saved === 'dark' || saved === 'light') return saved;
    return 'light';
  });

  const [themeShape, setThemeShapeState] = useState<'square' | 'rounded'>(() => {
    const saved = localStorage.getItem('app_theme_shape');
    if (saved === 'square' || saved === 'rounded') return saved;
    return 'square';
  });

  const toggleThemeMode = (mode: 'light' | 'dark') => {
    setThemeModeState(mode);
    localStorage.setItem('app_theme_mode', mode);
    document.documentElement.setAttribute('data-theme', mode);
  };

  const toggleThemeShape = (shape: 'square' | 'rounded') => {
    setThemeShapeState(shape);
    localStorage.setItem('app_theme_shape', shape);
    document.documentElement.setAttribute('data-shape', shape);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    document.documentElement.setAttribute('data-shape', themeShape);
  }, [themeMode, themeShape]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await getUserEmpresaAPI();
        if (cancelled) return;
        const empresaKey = data.empresa?.toUpperCase() || 'KADESH';

        localStorage.setItem('theme_empresa_cache', empresaKey);

        setEmpresa(empresaKey);
        setTheme(THEMES[empresaKey] || THEMES.KADESH);
      } catch (err) {
        if (!cancelled) {
          setTheme(THEMES.KADESH);
          localStorage.setItem('theme_empresa_cache', 'KADESH');
        }
      } finally {
        if (!cancelled) setThemeLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  // Aplica as cores como CSS custom properties no :root
  useLayoutEffect(() => {
    const root = document.documentElement;

    if (themeMode !== 'dark') {
      root.style.setProperty('--theme-primary', theme.primary);
      root.style.setProperty('--theme-primary-light', theme.primaryLight);
      root.style.setProperty('--theme-primary-dark', theme.primaryDark);
      root.style.setProperty('--theme-accent', theme.accent);
      root.style.setProperty('--theme-chart-start', theme.chartGradient[0]);
      root.style.setProperty('--theme-chart-end', theme.chartGradient[1]);
      root.style.setProperty('--kpi-progress-color', theme.primary);
      root.style.setProperty('--sidebar-bg', theme.sidebarBg);
      root.style.setProperty('--sidebar-link-color', theme.sidebarLinkColor);
      root.style.setProperty('--sidebar-active-bg', theme.sidebarActiveBg);
      root.style.setProperty('--sidebar-active-color', theme.sidebarActiveColor);
      root.style.setProperty('--sidebar-active-border-color', theme.sidebarActiveBorderColor);
      root.style.setProperty('--mobile-menu-bg', theme.sidebarActiveBg);
      root.style.setProperty('--skeleton-bg', '#e2e8f0');
      root.style.setProperty('--skeleton-shimmer', '#f1f5f9');
      root.style.setProperty('--skeleton-card-bg', 'var(--bg-widget)');
    } else {
      const isKadesh = theme.name === 'Kadesh';
      const darkPrimary = isKadesh ? '#dc2626' : theme.primary;
      const darkPrimaryLight = isKadesh ? '#ef4444' : theme.primaryLight;
      const darkPrimaryDark = isKadesh ? '#991b1b' : theme.primaryDark;
      const darkAccent = '#234278'; // Azul Navy mais claro no tema escuro

      root.style.setProperty('--theme-primary', darkPrimary);
      root.style.setProperty('--theme-primary-light', darkPrimaryLight);
      root.style.setProperty('--theme-primary-dark', darkPrimaryDark);
      root.style.setProperty('--theme-accent', darkAccent);
      root.style.setProperty('--theme-chart-start', theme.chartGradient[0]);
      root.style.setProperty('--theme-chart-end', theme.chartGradient[1]);
      root.style.setProperty('--kpi-progress-color', darkPrimary);
      root.style.setProperty('--sidebar-bg', '#181818');
      root.style.setProperty('--sidebar-link-color', '#a3a3a3');
      root.style.setProperty('--sidebar-active-bg', theme.name === 'Imbiseg' ? darkAccent : (theme.sidebarActiveBg === '#fdfdff' ? darkPrimary : theme.sidebarActiveBg));
      root.style.setProperty('--sidebar-active-color', '#ffffff');
      root.style.setProperty('--sidebar-active-border-color', theme.name === 'Imbiseg' ? darkAccent : theme.sidebarActiveBorderColor);
      root.style.setProperty('--mobile-menu-bg', theme.name === 'Imbiseg' ? darkAccent : theme.mobileMenuBg);
      root.style.setProperty('--skeleton-bg', '#2a2a2a');
      root.style.setProperty('--skeleton-shimmer', '#3a3a3a');
      root.style.setProperty('--skeleton-card-bg', '#1e1e1e');
    }
    root.style.setProperty('--sidebar-shadow', theme.sidebarShadow);

    // Sidebar logo properties
    root.style.setProperty('--sidebar-logo-height', theme.sidebarLogoHeight);
    root.style.setProperty('--sidebar-logo-mobile-height', theme.sidebarLogoMobileHeight);
    root.style.setProperty('--sidebar-logo-padding-x', theme.sidebarLogoPaddingX);
    root.style.setProperty('--sidebar-logo-padding-bottom', theme.sidebarLogoPaddingBottom);

    return () => {
      // Cleanup opcional
    };
  }, [theme, themeMode]);

  return { theme, empresa, loading, themeMode, toggleThemeMode, themeShape, toggleThemeShape };
}

